import { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';

interface OptionsImage {
  onUploadImage: (blob: Blob) => Promise<string>;
  onRognerImage: (srcActuel: string) => Promise<string | null>;
}

// Image standard + attribut "width" (persisté dans le JSON de la page), une poignée de
// redimensionnement (bas-droite) et une mini barre d'outils (rotation immédiate via canvas, rognage
// délégué à wiki.html qui gère la modale) -- pas de dépendance externe, NodeView vanilla (pas de React
// dans ce bundle). onUploadImage/onRognerImage sont injectés via .configure() à chaque montage.
const ImageRedimensionnable = Image.extend<OptionsImage>({
  addOptions() {
    return {
      ...this.parent?.(),
      onUploadImage: async () => { throw new Error('onUploadImage non configuré'); },
      onRognerImage: async () => null,
    };
  },
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (element: HTMLElement) => element.style.width || element.getAttribute('width'),
        renderHTML: (attrs: any) => attrs.width ? { style: `width: ${attrs.width}` } : {},
      },
    };
  },
  addNodeView() {
    const optionsExt = this.options;

    return ({ node, getPos, editor }: any) => {
      let nodeCourant = node;

      const wrapper = document.createElement('div');
      wrapper.className = 'wiki-image-wrapper';

      const img = document.createElement('img');
      img.src = nodeCourant.attrs.src;
      if (nodeCourant.attrs.alt) img.alt = nodeCourant.attrs.alt;
      if (nodeCourant.attrs.width) img.style.width = nodeCourant.attrs.width;
      wrapper.appendChild(img);

      function appliquerNouvelleSrc(url: string) {
        img.src = url;
        img.style.width = '';
        const pos = typeof getPos === 'function' ? getPos() : undefined;
        if (typeof pos === 'number') {
          editor.view.dispatch(
            editor.view.state.tr.setNodeMarkup(pos, undefined, { ...nodeCourant.attrs, src: url, width: null })
          );
        }
      }

      async function pivoter(degres: number) {
        try {
          const imgTmp = new (window as any).Image();
          imgTmp.src = img.src;
          await new Promise((resolve, reject) => { imgTmp.onload = resolve; imgTmp.onerror = reject; });
          const w = imgTmp.naturalWidth, h = imgTmp.naturalHeight;
          const canvas = document.createElement('canvas');
          canvas.width = h;
          canvas.height = w;
          const ctx = canvas.getContext('2d')!;
          ctx.translate(canvas.width / 2, canvas.height / 2);
          ctx.rotate((degres * Math.PI) / 180);
          ctx.drawImage(imgTmp, -w / 2, -h / 2);
          const blob: Blob = await new Promise((resolve) => canvas.toBlob((b) => resolve(b as Blob), 'image/png'));
          const nouvelleUrl = await optionsExt.onUploadImage(blob);
          appliquerNouvelleSrc(nouvelleUrl);
        } catch (e) {
          console.error('🔴 Erreur de rotation image :', e);
        }
      }

      async function rogner() {
        try {
          const nouvelleUrl = await optionsExt.onRognerImage(img.src);
          if (nouvelleUrl) appliquerNouvelleSrc(nouvelleUrl);
        } catch (e) {
          console.error('🔴 Erreur de rognage image :', e);
        }
      }

      if (editor.isEditable) {
        const barreOutils = document.createElement('div');
        barreOutils.className = 'wiki-image-toolbar';

        const boutonOutil = (label: string, titre: string, action: () => void) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.title = titre;
          btn.textContent = label;
          btn.addEventListener('pointerdown', (e) => e.stopPropagation());
          btn.addEventListener('click', (e) => { e.preventDefault(); action(); });
          return btn;
        };

        barreOutils.appendChild(boutonOutil('⟲', 'Pivoter à gauche', () => pivoter(-90)));
        barreOutils.appendChild(boutonOutil('⟳', 'Pivoter à droite', () => pivoter(90)));
        barreOutils.appendChild(boutonOutil('✂', 'Rogner', () => rogner()));
        wrapper.appendChild(barreOutils);

        const poignee = document.createElement('span');
        poignee.className = 'wiki-image-resize-handle';
        wrapper.appendChild(poignee);

        poignee.addEventListener('pointerdown', (e: PointerEvent) => {
          e.preventDefault();
          e.stopPropagation();
          const departX = e.clientX;
          const largeurDepart = img.offsetWidth;
          const largeurMax = (editor.view.dom as HTMLElement).clientWidth;

          const onDeplacer = (ev: PointerEvent) => {
            const nouvelleLargeur = Math.min(largeurMax, Math.max(80, largeurDepart + (ev.clientX - departX)));
            img.style.width = `${nouvelleLargeur}px`;
          };
          const onRelacher = () => {
            document.removeEventListener('pointermove', onDeplacer);
            document.removeEventListener('pointerup', onRelacher);
            const pos = typeof getPos === 'function' ? getPos() : undefined;
            if (typeof pos === 'number') {
              editor.view.dispatch(
                editor.view.state.tr.setNodeMarkup(pos, undefined, { ...nodeCourant.attrs, width: img.style.width })
              );
            }
          };
          document.addEventListener('pointermove', onDeplacer);
          document.addEventListener('pointerup', onRelacher);
        });
      }

      return {
        dom: wrapper,
        update: (updatedNode: any) => {
          if (updatedNode.type.name !== 'image') return false;
          nodeCourant = updatedNode;
          img.src = updatedNode.attrs.src;
          img.style.width = updatedNode.attrs.width || '';
          return true;
        },
      };
    };
  },
});

// Bundle autonome chargé en <script> classique (pas d'ESM côté public/, cf. CSP script-src 'self' sans
// CDN dédié à l'éditeur) -- expose un seul global window.SamuelWikiEditor consommé par wiki.html.
interface MontageOptions {
  contenu?: any;
  editable: boolean;
  onChange?: (json: any) => void;
  onUploadImage?: (blob: Blob) => Promise<string>;
  onRognerImage?: (srcActuel: string) => Promise<string | null>;
}

function monter(conteneur: HTMLElement, options: MontageOptions) {
  const editor = new Editor({
    element: conteneur,
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false }),
      ImageRedimensionnable.configure({
        onUploadImage: options.onUploadImage,
        onRognerImage: options.onRognerImage,
      }),
    ],
    content: options.contenu || '',
    editable: options.editable,
    onUpdate: ({ editor }) => {
      options.onChange?.(editor.getJSON());
    },
  });

  return {
    getJSON: () => editor.getJSON(),
    setContenu: (contenu: any) => editor.commands.setContent(contenu || '', { emitUpdate: false }),
    setEditable: (editable: boolean) => editor.setEditable(editable),
    detruire: () => editor.destroy(),
    // Actions de la barre d'outils, appelées directement par wiki.html
    commandes: {
      gras: () => editor.chain().focus().toggleBold().run(),
      italique: () => editor.chain().focus().toggleItalic().run(),
      titre: (niveau: 1 | 2 | 3) => editor.chain().focus().toggleHeading({ level: niveau }).run(),
      listePuces: () => editor.chain().focus().toggleBulletList().run(),
      listeNumerotee: () => editor.chain().focus().toggleOrderedList().run(),
      citation: () => editor.chain().focus().toggleBlockquote().run(),
      lien: (url: string) => editor.chain().focus().setLink({ href: url }).run(),
      image: (url: string) => editor.chain().focus().setImage({ src: url }).run(),
      annuler: () => editor.chain().focus().undo().run(),
      retablir: () => editor.chain().focus().redo().run(),
    },
  };
}

(window as any).SamuelWikiEditor = { monter };
